import useSWR, { mutate } from 'swr'
import { posApiFetch } from '@/utils/pos-api'
import { EntryPassTemplate, EntryPassTemplateFormData } from '@/constants'
import { useToast } from './use-toast'
import { useState } from 'react'

const TEMPLATES_ENDPOINT = '/v1/entry-passes/templates'

interface TemplatesResponse {
  success: boolean
  data: EntryPassTemplate[]
}

export const useEntryPassTemplates = () => {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data, error, isLoading } = useSWR<TemplatesResponse>(
    TEMPLATES_ENDPOINT,
    async () => {
      const response = await posApiFetch(TEMPLATES_ENDPOINT)
      return response.json()
    }
  )

  const createTemplate = async (formData: EntryPassTemplateFormData): Promise<boolean> => {
    setIsSubmitting(true)
    try {
      const response = await posApiFetch(TEMPLATES_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Entry pass template created successfully'
        })
        mutate(TEMPLATES_ENDPOINT)
        return true
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to create template')
      }
    } catch (error) {
      console.error('Create template error:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create template',
        variant: 'destructive'
      })
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const updateTemplate = async (id: number, formData: EntryPassTemplateFormData): Promise<boolean> => {
    setIsSubmitting(true)
    try {
      const response = await posApiFetch(`${TEMPLATES_ENDPOINT}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Entry pass template updated successfully'
        })
        mutate(TEMPLATES_ENDPOINT)
        return true
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update template')
      }
    } catch (error) {
      console.error('Update template error:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update template',
        variant: 'destructive'
      })
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const deleteTemplate = async (id: number): Promise<boolean> => {
    try {
      const response = await posApiFetch(`${TEMPLATES_ENDPOINT}/${id}`, {
        method: 'DELETE',
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Entry pass template deleted successfully'
        })
        mutate(TEMPLATES_ENDPOINT)
        return true
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to delete template')
      }
    } catch (error) {
      console.error('Delete template error:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete template',
        variant: 'destructive'
      })
      return false
    }
  }

  const toggleActive = async (id: number, isActive: boolean): Promise<boolean> => {
    try {
      const response = await posApiFetch(`${TEMPLATES_ENDPOINT}/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive })
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Entry pass template ${isActive ? 'activated' : 'deactivated'} successfully`
        })
        mutate(TEMPLATES_ENDPOINT)
        return true
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update template status')
      }
    } catch (error) {
      console.error('Toggle active error:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update template status',
        variant: 'destructive'
      })
      return false
    }
  }

  return {
    templates: data?.data || [],
    isLoading,
    isSubmitting,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleActive,
    refresh: () => mutate(TEMPLATES_ENDPOINT)
  }
}
